'use client';

export type FilterKey = 'all' | 'at_risk' | 'this_week' | 'next_week' | 'unscheduled' | 'complete';
export type SortKey = 'install_date_asc' | 'install_date_desc' | 'readiness_desc' | 'readiness_asc' | 'vpm' | 'crew';

export interface QueueControlsProps {
  activeFilter: FilterKey;
  onFilterChange: (filter: FilterKey) => void;
  sortKey: SortKey;
  onSortChange: (sort: SortKey) => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  counts: Record<FilterKey, number>;
  totalJobs: number;
  visibleJobs: number;
}

const FILTER_TABS: { key: FilterKey; label: string }[] = [
  { key: 'all',         label: 'All' },
  { key: 'at_risk',     label: 'At Risk' },
  { key: 'this_week',   label: 'This Week' },
  { key: 'next_week',   label: 'Next Week' },
  { key: 'unscheduled', label: 'Unscheduled' },
  { key: 'complete',    label: 'Complete' },
];

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'install_date_asc',  label: 'Install Date (soonest)' },
  { key: 'install_date_desc', label: 'Install Date (latest)' },
  { key: 'readiness_desc',    label: 'Readiness (best first)' },
  { key: 'readiness_asc',     label: 'Readiness (worst first)' },
  { key: 'vpm',               label: 'VPM' },
  { key: 'crew',              label: 'Crew' },
];

export default function QueueControls({
  activeFilter,
  onFilterChange,
  sortKey,
  onSortChange,
  searchQuery,
  onSearchChange,
  counts,
}: QueueControlsProps) {
  return (
    <div className="mb-4 bg-white rounded-xl shadow-sm border border-gray-100 p-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">

        {/* Filter tabs — scrollable on mobile */}
        <div className="flex items-center gap-1 overflow-x-auto -mx-1 px-1 lg:flex-1">
          {FILTER_TABS.map(tab => {
            const isActive = tab.key === activeFilter;
            const count = counts[tab.key] ?? 0;
            const isAtRiskWithCount = tab.key === 'at_risk' && !isActive && count > 0;

            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => onFilterChange(tab.key)}
                className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-[#1C2E5E] text-white shadow-sm'
                    : isAtRiskWithCount
                    ? 'bg-red-50 text-red-700 hover:bg-red-100' :'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                <span className={`inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full text-[10px] font-bold ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : isAtRiskWithCount
                    ? 'bg-red-100 text-red-700 border border-red-200' :'bg-white text-gray-500 border border-gray-200'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Right cluster: search + sort */}
        <div className="flex items-center gap-2 lg:flex-shrink-0">
          {/* Search input */}
          <div className="relative flex-1 lg:w-64">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-3.5-3.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Search J-number, address, or name…"
              className="w-full pl-8 pr-8 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              aria-label="Search jobs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => onSearchChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>

          {/* Sort select */}
          <div className="relative">
            <select
              value={sortKey}
              onChange={e => onSortChange(e.target.value as SortKey)}
              className="pl-3 pr-8 py-2 text-[12px] rounded-lg border border-gray-200 bg-gray-50 hover:bg-white focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all appearance-none cursor-pointer font-semibold text-gray-700"
              aria-label="Sort jobs by"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            <svg
              className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

      </div>
    </div>
  );
}
